import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getPublicStatusPage } from "../lib/api";
import UptimeBar, { DayInfo } from "../components/UptimeBar";
import { MonitorCurrentStatus, PublicMonitor } from "../types";
import Stars from "../components/Stars";
import { Bone } from "../components/Skeleton";

function statusDot(status: MonitorCurrentStatus) {
  const colors: Record<string, string> = {
    operational: "#39D98A",
    degraded:    "#FDDD48",
    down:        "#FF5C5C",
    unknown:     "#98A1AB",
  };
  return (
    <span style={{
      display: "inline-block", width: 10, height: 10, borderRadius: "50%",
      background: colors[status] ?? "#98A1AB",
      flexShrink: 0,
    }} />
  );
}

function statusLabel(status: MonitorCurrentStatus): string {
  return ({ operational: "Operational", degraded: "Degraded", down: "Down", unknown: "Checking…" })[status] ?? status;
}

function overallBanner(status: MonitorCurrentStatus) {
  const configs: Record<string, { text: string; bg: string; color: string }> = {
    operational: { text: "All Systems Operational", bg: "rgba(57,217,138,0.1)",  color: "#39D98A" },
    degraded:    { text: "Partial Outage",           bg: "rgba(253,221,72,0.1)",  color: "#FDDD48" },
    down:        { text: "Major Outage",             bg: "rgba(255,92,92,0.1)",   color: "#FF5C5C" },
    unknown:     { text: "Checking Status…",         bg: "rgba(152,161,171,0.1)", color: "#98A1AB" },
  };
  const cfg = configs[status] ?? configs.unknown;
  return (
    <div style={{
      padding: "18px 24px", borderRadius: 6,
      background: cfg.bg, border: `1px solid ${cfg.color}44`,
      display: "flex", alignItems: "center", gap: 12, marginBottom: 32,
    }}>
      <span style={{ width: 14, height: 14, borderRadius: "50%", background: cfg.color, display: "inline-block", flexShrink: 0 }} />
      <span style={{ fontSize: 18, fontWeight: 700, color: cfg.color }}>{cfg.text}</span>
    </div>
  );
}

function MonitorRow({ monitor }: { monitor: PublicMonitor }) {
  const days: DayInfo[] = monitor.days.map((d) => ({
    date: d.date,
    status: d.status,
    upCount: d.up_count,
    downCount: d.down_count,
  }));

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {statusDot(monitor.current_status)}
          <span style={{ fontSize: 16, fontWeight: 600, color: "#EFF2F5" }}>{monitor.name}</span>
        </div>
        <span style={{
          fontSize: 12, fontWeight: 600, color: "#98A1AB",
          background: "rgba(255,255,255,0.05)", borderRadius: 3, padding: "3px 8px",
        }}>
          {statusLabel(monitor.current_status)}
        </span>
      </div>
      <UptimeBar days={days} uptimePct={monitor.uptime_pct} />
    </div>
  );
}

export default function StatusPagePublic() {
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-status-page", slug],
    queryFn: () => getPublicStatusPage(slug!),
    enabled: !!slug,
    refetchInterval: 60_000,
  });

  return (
    <div style={{
      minHeight: "100vh",
      color: "#EFF2F5",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif",
    }}>
      <Stars />
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 24px 80px", position: "relative", zIndex: 1 }}>
        {isLoading ? (
          <>
            <div style={{ marginBottom: 32 }}>
              <Bone w={220} h={28} style={{ marginBottom: 10 }} />
              <Bone w={300} h={14} />
            </div>
            <Bone w="100%" h={56} br={6} style={{ marginBottom: 32 }} />
            {[160, 200, 180].map((w, i) => (
              <div key={i} style={{ marginBottom: 32 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <Bone w={w} h={16} />
                  <Bone w={80} h={22} />
                </div>
                <Bone w="100%" h={28} />
              </div>
            ))}
          </>
        ) : isError || !data ? (
          <div style={{ textAlign: "center", paddingTop: 80 }}>
            <p style={{ fontSize: 18, color: "#EFF2F5", fontWeight: 600 }}>Page not found</p>
            <p style={{ fontSize: 14, color: "#98A1AB" }}>This status page doesn't exist or is no longer public.</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 800, color: "#EFF2F5" }}>{data.title}</h1>
              {data.description && (
                <p style={{ margin: 0, fontSize: 15, color: "#98A1AB", lineHeight: 1.6 }}>{data.description}</p>
              )}
            </div>

            {overallBanner(data.overall_status)}

            {data.monitors.length === 0 ? (
              <p style={{ color: "#98A1AB", fontSize: 14 }}>No monitors configured for this page yet.</p>
            ) : (
              <div>
                {data.monitors.map((m, i) => (
                  <MonitorRow key={i} monitor={m} />
                ))}
              </div>
            )}

            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 24, marginTop: 16, textAlign: "center" }}>
              <span style={{ fontSize: 12, color: "#98A1AB" }}>
                Powered by{" "}
                <Link to="/" style={{ color: "#98A1AB", textDecoration: "none", fontWeight: 600 }}>WorldRTT</Link>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
